// "Entrar como" do /admin (16/10/2026).
// O admin entra na conta de um paciente ou médico para ver exatamente o que a pessoa vê.
// O token do admin fica guardado aqui; a conta da pessoa usa um token curto (2h) que o
// servidor derruba na hora em que o admin clica em "Voltar ao admin". Toda sessão exige
// motivo e fica registrada (admin_impersonations + auditoria).
import { safeGetItem, safeRemoveItem, safeSetItem } from "@/lib/safeStorage";
import { getToken, persistPatientSession, setToken } from "@/lib/api";

const KEY = "clinica_impersonation";

export interface ImpersonationSession {
  admin_token: string;
  impersonation_id: number | string;
  kind: "patient" | "doctor";
  target_id: string | number;
  target_name: string;
  return_url: string;
  started_at: string;
  expires_at: string;
}

export function getImpersonation(): ImpersonationSession | null {
  try {
    const raw = safeGetItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as ImpersonationSession;
    return s?.admin_token ? s : null;
  } catch {
    return null;
  }
}

/** Troca a sessão para a conta da pessoa (recarrega a página na área dela). */
export function beginImpersonation(res: {
  token: string; impersonation_id: number | string; expires_at: string; redirect: string;
  target: { kind: "patient" | "doctor"; id: string | number; name: string };
}, returnUrl: string): void {
  const adminToken = getToken();
  if (!adminToken) throw new Error("Sessão do admin não encontrada.");
  const session: ImpersonationSession = {
    admin_token: adminToken,
    impersonation_id: res.impersonation_id,
    kind: res.target.kind,
    target_id: res.target.id,
    target_name: res.target.name,
    return_url: returnUrl,
    started_at: new Date().toISOString(),
    expires_at: res.expires_at,
  };
  safeSetItem(KEY, JSON.stringify(session));
  if (res.target.kind === "patient") persistPatientSession(res.token, res.target.id);
  else {
    safeRemoveItem("logged_patient_case_id");
    setToken(res.token);
  }
  window.location.href = res.redirect;
}

/** Encerra no servidor (o token da pessoa para de valer) e volta para o /admin. */
export async function endImpersonation(): Promise<void> {
  const s = getImpersonation();
  if (!s) return;
  try {
    await fetch(`/api/admin/impersonate/${encodeURIComponent(String(s.impersonation_id))}/end`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${s.admin_token}` },
      body: "{}",
    });
  } catch { /* mesmo sem rede o token expira sozinho em 2h; a volta segue */ }
  safeRemoveItem("logged_patient_case_id");
  safeRemoveItem(KEY);
  setToken(s.admin_token);
  window.location.href = s.return_url || "/admin";
}
